import {
  AlertCircle,
  Bluetooth,
  Calendar,
  CheckCircle,
  Code,
  Cpu,
  FileText,
  Globe,
  Layers,
  Layout,
  LifeBuoy,
  MapPin,
  PenTool,
  PlaySquare,
  Radio,
  RefreshCw,
  Search,
  Server,
  Shield,
  ShieldCheck,
  ShoppingBag,
  Smartphone,
  Target,
  TrendingUp,
  Users,
  Zap,
} from "lucide-react";

import type { AboutUsCopy } from "@components/bluetooth-landing/AboutUs";
import type { FAQCopy } from "@components/bluetooth-landing/FAQ";
import type { FooterCopy } from "@components/bluetooth-landing/Footer";
import type { HeroCopy } from "@components/bluetooth-landing/Hero";
import type { NavbarCopy } from "@components/bluetooth-landing/Navbar";
import type { ProcessCopy } from "@components/bluetooth-landing/Process";
import type { ServicesCopy } from "@components/bluetooth-landing/Services";
import type { TargetAudienceCopy } from "@components/bluetooth-landing/TargetAudience";
import type { ValuePropositionCopy } from "@components/bluetooth-landing/ValueProposition";
import { faqsEn } from "@data/firmware-landing/faq";

export const title =
  "Firmware Development Company for Bluetooth LE | Blues Brackets";
export const description =
  "Embedded firmware development company specializing in Nordic nRF52, nRF53, and nRF54 Bluetooth LE devices. We build production-grade firmware and the companion mobile app — full stack from chip to app store.";

export const structuredData = [
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Blues Brackets",
    "url": "https://bluesbrackets.com",
    "logo": "https://bluesbrackets.com/favicon-dark.png",
    description,
    "areaServed": "EU",
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "sales",
      "email": "hello@bluesbrackets.com",
      "availableLanguage": ["Polish", "English"],
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Embedded firmware development for Bluetooth LE devices",
    "provider": {
      "@type": "Organization",
      "name": "Blues Brackets",
    },
    description,
    "areaServed": "EU",
    "serviceType": "Embedded firmware development",
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqsEn.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a,
      },
    })),
  },
];

export const navbarCopy: NavbarCopy = {
  links: {
    targetAudience: "Who it is for",
    values: "Our expertise",
    services: "Services",
    process: "Process",
    about: "About us",
    faq: "FAQ",
  },
  cta: "Estimate your project",
};

export const heroCopy: HeroCopy = {
  header: "Embedded firmware development",
  headerTail: "for Bluetooth LE devices",
  description:
    "We build production-grade firmware for Nordic nRF52, nRF53, and nRF54 — and the companion mobile app that talks to it. One team responsible for the full stack, from GATT profile to App Store.",
  cta: {
    text: "Free consultation",
    href: "#kontakt",
  },
  secondaryCta: {
    text: "See our services",
    href: "#uslugi",
  },
  benefits: [
    { text: "nRF52 / nRF53 / nRF54 specialists", Icon: CheckCircle },
    { text: "Firmware + mobile app in one team", Icon: CheckCircle },
    { text: "EU GDPR ready", Icon: CheckCircle },
  ],
};

export const targetAudienceCopy: TargetAudienceCopy = {
  header: {
    h2: "Who we work with",
    h3: "Who are our firmware development services for?",
    p: "We work with hardware teams, startups, and product companies that need reliable embedded firmware and the software stack around it. Does one of these fit your situation?",
  },
  points: [
    {
      Icon: Cpu,
      title: "You are building a new BLE product from scratch",
      description:
        "You have chosen your Nordic chip and need a firmware team that knows the nRF Connect SDK, Zephyr RTOS, and BLE stack inside out.",
    },
    {
      Icon: AlertCircle,
      title: "Your existing firmware is unstable or unmaintained",
      description:
        "Connection drops, unexpected resets, or a codebase no one dares to touch. We diagnose the root cause and bring it back under control.",
    },
    {
      Icon: Smartphone,
      title: "You need firmware and a mobile app",
      description:
        "You want one partner responsible for both sides of the BLE link — so the GATT profile, protocol design, and app logic are consistent from day one.",
    },
    {
      Icon: RefreshCw,
      title: "You are migrating to nRF53 or nRF54",
      description:
        "Upgrading from an nRF52 series to nRF5340 or nRF54L15 brings new toolchains and architectural changes. We help you plan and execute the migration.",
    },
    {
      Icon: Target,
      title: "You need OTA updates in production",
      description:
        "Shipping firmware updates to devices already in the field requires a secure bootloader, DFU protocol, and a mobile app that can manage the process reliably.",
    },
  ],
  cta: {
    h4: "Sounds like your project?",
    text: "Tell us about your hardware and what the firmware needs to do. We will help you scope the work and find the right approach.",
    href: "#kontakt",
    buttonText: "Book a free consultation",
  },
};

export const valuePropositionCopy: ValuePropositionCopy = {
  header: {
    h2: "Our expertise",
    h3: "Why choose us as your firmware development company?",
    p: "We specialise in Bluetooth LE firmware on Nordic hardware. We also build the mobile app. Most embedded shops stop at the device — we cover the complete product.",
    imageAlt: "Embedded firmware development on Nordic nRF hardware",
  },
  values: [
    {
      Icon: Cpu,
      title: "Nordic nRF52, nRF53, nRF54 specialists",
      description:
        "We work daily with the nRF Connect SDK, Zephyr RTOS, and the Nordic BLE stack. nRF52832, nRF52840, nRF5340, nRF54L15, nRF54H20 — we know their differences, peripheral sets, and the gotchas that waste weeks of bring-up time.",
    },
    {
      Icon: Bluetooth,
      title: "Deep Bluetooth LE expertise",
      description:
        "Custom GATT profiles, connection parameter tuning, power optimization, OTA DFU, BLE Mesh, and Auracast (LE Audio). We design the BLE protocol layer so it works reliably in the real world, not just on the bench.",
    },
    {
      Icon: Smartphone,
      title: "Full stack: firmware and mobile app",
      description:
        "We design the GATT profile and then implement it in both the firmware and the iOS/Android app. No handover risk, no finger-pointing between teams — one partner accountable for the complete BLE product.",
    },
    {
      Icon: Shield,
      title: "Production-grade quality",
      description:
        "We write firmware intended to ship in volume and run unsupervised in the field. That means secure bootloaders, watchdog handling, systematic power profiling, and thorough testing with real hardware.",
    },
    {
      Icon: Globe,
      title: "European team, EU data standards",
      description:
        "We are based in Poland and understand the regulatory landscape for products sold in the EU. GDPR-compliant cloud, English and Polish communication, and alignment with CE and Bluetooth SIG requirements.",
    },
  ],
};

export const servicesCopy: ServicesCopy = {
  header: {
    h2: "Services",
    h3: "Custom firmware development services",
    p: "From architecture and GATT design to production testing and OTA updates. Our embedded firmware development service covers every stage of the firmware lifecycle.",
  },
  services: [
    {
      Icon: Search,
      title: "Firmware architecture and consulting",
      description:
        "We review your hardware design, define the firmware architecture, choose the right RTOS configuration, and design the BLE GATT profile before a line of code is written — reducing risk from the start.",
    },
    {
      Icon: Code,
      title: "Custom firmware development",
      description:
        "We write production firmware for Nordic nRF52, nRF53, and nRF54 using the nRF Connect SDK and Zephyr RTOS. Peripheral drivers, application logic, BLE stack configuration, and power management.",
    },
    {
      Icon: Bluetooth,
      title: "Bluetooth LE integration",
      description:
        "Custom GATT profiles, connection and advertising configuration, bonding and pairing, notification and indication handling, and BLE Mesh or Auracast for more complex topologies.",
    },
    {
      Icon: Zap,
      title: "OTA firmware updates (DFU)",
      description:
        "We implement the Nordic Secure Bootloader and nRF DFU protocol so you can push firmware updates to devices in the field via the companion mobile app — securely and without physical access.",
    },
    {
      Icon: Smartphone,
      title: "Companion mobile app development",
      description:
        "We build the iOS and Android app (Flutter) that pairs with your device. One team owns the GATT protocol on both ends, so integration works reliably from day one.",
    },
    {
      Icon: Layers,
      title: "Hardware bring-up and testing",
      description:
        "We bring up new PCB revisions, write hardware abstraction layers for custom peripherals, and build automated test rigs that verify firmware behaviour on real hardware.",
    },
    {
      Icon: TrendingUp,
      title: "Power optimization",
      description:
        "Battery life is a product feature. We profile current draw, tune advertising intervals, implement sleep modes, and measure the result on real hardware — not just in simulation.",
    },
    {
      Icon: LifeBuoy,
      title: "Firmware maintenance and support",
      description:
        "After launch we handle ongoing firmware maintenance, compatibility updates for new nRF SDK and Zephyr releases, bug fixes, and iterative feature development as your product evolves.",
    },
  ],
  cta: {
    h4: "Not sure what you need?",
    p: "Send us your hardware specs and a brief description of the firmware requirements. We will come back with an honest assessment and a scoping proposal.",
    button: {
      text: "Book a free consultation",
      href: "#kontakt",
    },
  },
};

export const processCopy: ProcessCopy = {
  header: {
    h2: "Our process",
    h3: "How a firmware project runs",
    p: "Clear stages, defined deliverables, and hardware in the loop from day one. Here is how we go from first contact to production-ready firmware.",
  },
  stepNumber: "Step",
  steps: [
    {
      Icon: Calendar,
      title: "Free online meeting",
      duration: "within a few hours",
      description:
        "A 30-minute call with a firmware engineer. You describe the hardware and requirements; we give you an honest first assessment and indicative estimate.",
    },
    {
      Icon: FileText,
      title: "Architecture workshop",
      duration: "within a few days",
      description:
        "After a 2-hour online session you receive a written report covering firmware architecture, GATT profile design, risk areas, and a detailed project estimate.",
    },
    {
      Icon: Search,
      title: "Hardware bring-up",
      duration: "1–2 weeks",
      description:
        "We verify the hardware, bring up the basic firmware skeleton, confirm BLE communication, and document the peripheral interfaces before full development begins.",
    },
    {
      Icon: PenTool,
      title: "Contract signing",
      duration: "schedule agreement",
      description:
        "We define milestones, commit to delivery dates, and agree on how progress is communicated throughout the project.",
    },
    {
      Icon: Code,
      title: "Stage-by-stage firmware development",
      duration: "min. 2–3 months",
      description:
        "Peripheral drivers, BLE stack, application logic, OTA DFU, and power optimization — delivered in stages with real hardware testing and regular progress reviews.",
    },
    {
      Icon: PlaySquare,
      title: "Mobile app integration",
      duration: "parallel or sequential",
      description:
        "The companion iOS and Android app is built alongside or after the firmware, using the same GATT profile design so both sides work correctly together.",
    },
    {
      Icon: ShoppingBag,
      title: "Production testing and store release",
      duration: "min. 2 weeks",
      description:
        "We support manufacturing test procedures, DFU validation, and App Store / Google Play release of the companion app.",
    },
    {
      Icon: CheckCircle,
      title: "Handover and ongoing support",
      duration: "ongoing",
      description:
        "You receive the full source code and documentation, or we continue as your firmware maintenance partner for the long term.",
    },
  ],
  cta: {
    href: "#kontakt",
    text: "Start your firmware project",
  },
};

export const aboutUsCopy: AboutUsCopy = {
  header: {
    h2: "About us",
    h3: "A firmware development company that also ships the app",
    p1: "We are a small, senior team from Poland with hands-on experience in embedded firmware, Bluetooth LE, and mobile app development.",
    p2: "Michał and Tomasz lead firmware and mobile development, Kuba handles backend and infrastructure, and Mateusz designs the UX that makes hardware products intuitive to use.",
    p3: "GDPR compliant",
    p4: "Data in the EU",
    Icon: ShieldCheck,
    imageAlt: "Firmware development team working on Nordic nRF hardware",
  },
  values: [
    {
      Icon: Users,
      title: "You work directly with the engineers",
      description:
        "No account managers or outsourcing layers. The person on the call is the person writing the firmware.",
    },
    {
      Icon: MapPin,
      title: "European team, EU regulations",
      description:
        "Based in Poland, we understand CE requirements, GDPR compliance, and what it takes to launch a hardware product in the EU.",
    },
    {
      Icon: Radio,
      title: "We cover firmware and the full stack",
      description:
        "From nRF chip selection and firmware to mobile app and backend — one partner for the complete BLE product reduces integration risk significantly.",
    },
  ],
  cta: {
    text: "Contact us",
    href: "#kontakt",
  },
};

export const faqCopy: FAQCopy = {
  header: {
    h2: "FAQ",
    h3: "Frequently asked questions about firmware development",
  },
  faqs: faqsEn,
};

export const footerCopy: FooterCopy = {
  header: {
    h2: "Looking for a firmware development company?",
    p: "Book a free consultation, describe your hardware and requirements, and get an honest first assessment from an experienced firmware engineer.",
  },
  cta: {
    href: "#kontakt",
    text: "Book a free consultation",
    p: "We provide embedded firmware development services for Nordic nRF52, nRF53, and nRF54 Bluetooth LE devices — including the companion mobile app. Based in Poland, serving clients across Europe.",
    email: "hello@bluesbrackets.com",
    phone: "+48 535 462 678",
  },
  links: {
    h4: "Quick links",
    links: [
      { href: "#dla-kogo", text: "Who we work with" },
      { href: "#uslugi", text: "Services" },
      { href: "#proces", text: "Process" },
      { href: "#faq", text: "FAQ" },
      {
        href: "/mobile-app-with-bluetooth",
        text: "Mobile App with Bluetooth",
      },
      {
        href: "/flutter-app-development-company",
        text: "Flutter App Development",
      },
    ],
  },
  knowledgeBase: {
    h4: "Knowledge base",
    links: [
      {
        href: "/blogs/ble_embedded_development_platforms",
        text: "Best platforms for Bluetooth LE product development",
      },
      {
        href: "/blogs/auracast_mcu",
        text: "ESP32 Auracast: Does It Work? Best MCUs for LE Audio",
      },
      {
        href: "/blogs/ble_hardware_2026",
        text: "Best hardware for Bluetooth Low Energy BLE development in 2026",
      },
      {
        href: "/blogs/bluetooth_le_product_architecture",
        text: "Bluetooth LE IoT product architecture and development",
      },
      {
        href: "/blogs/ble_mobile_application_support",
        text: "BLE support on the mobile application side",
      },
      {
        href: "/blogs/is_bluetooth_le_right_for_your_product",
        text: "Is Bluetooth LE the right technology for your product or MVP?",
      },
    ],
  },
  copyright: "bluesBrackets. All rights reserved.",
  privacyPolicy: "Privacy Policy",
  privacyPolicyHref: "/privacy-policy",
  copiedLabel: "Copied to clipboard",
  contactForm: {
    heading: "Contact us",
    fields: {
      name: {
        label: "Full name",
        placeholder: "John Smith",
        requiredError: "Enter your full name",
      },
      email: {
        label: "Email address",
        placeholder: "john@company.com",
        requiredError: "Enter your email address",
        invalidError: "Enter a valid email address",
      },
      topic: {
        label: "Project description",
        placeholder:
          "Briefly describe your hardware, the chip you are using, and what the firmware needs to do...",
        requiredError: "Describe your project",
        maxLengthError: "The description can be up to 2000 characters long",
      },
    },
    submit: "Send inquiry",
    submitting: "Sending...",
    successMessage: "Message sent!",
    errorMessage: "Could not send the message :(",
    responseTime: "We reply within 24 hours on business days.",
    emailSubjectPrefix: "Full name",
  },
};
